import React, { useEffect, useState } from "react";
import { ShieldAlert } from "lucide-react";
import JeaCard from "@/components/jea/JeaCard";
import Skeleton from "@/components/jea/Skeleton";
import JeaButton from "@/components/jea/JeaButton";
import { demoClient, useDemo } from "@/pages/demo/DemoShell";
import { formatDate } from "@/lib/pricing";

const DemoBankruptcyPage = () => {
  const [state, setState] = useState({ snapshot: null, filings: [], loading: true });
  const demo = useDemo();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await demoClient.get("/demo/bankruptcy/portfolio");
        setState({
          snapshot: data.portfolio_snapshot,
          filings: data.active_filings || [],
          loading: false,
        });
      } catch { setState((s) => ({ ...s, loading: false })); }
    })();
  }, []);

  return (
    <div className="space-y-6" data-testid="demo-bankruptcy-page">
      <h1 className="text-[28px] font-bold text-white">Bankruptcy Watch</h1>

      {state.loading ? <Skeleton className="h-24" /> : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatTile label="Judgments Monitored" value={state.snapshot?.debtors_monitored ?? 0} />
          <StatTile label="Active Filings" value={state.snapshot?.active_filings_count ?? 0} color="#E74C3C" />
          <StatTile label="Unreviewed" value={state.snapshot?.unreviewed_count ?? 0} color="#F39C12" />
          <StatTile label="Last Sweep" value="just now" small />
        </div>
      )}

      <JeaCard className="border-l-4 border-warning" style={{ background: "rgba(243, 156, 18, 0.05)" }}>
        <div className="flex items-start gap-3">
          <ShieldAlert size={20} className="text-warning shrink-0 mt-1" />
          <div>
            <h3 className="text-warning font-bold text-[15px]">Automatic Stay in Effect (§ 362)</h3>
            <p className="text-text-secondary text-[13px] mt-1">
              When a debtor files for bankruptcy, all collection actions are automatically halted under
              11 U.S.C. § 362. Violating the stay can result in sanctions.
            </p>
          </div>
        </div>
      </JeaCard>

      <JeaCard data-tour="active-filings">
        <h2 className="text-white font-bold text-[16px] mb-3">Active Filings</h2>
        {state.loading ? <Skeleton className="h-40" /> : state.filings.length === 0 ? (
          <div className="text-text-secondary text-[13px]">No active bankruptcy filings.</div>
        ) : (
          <div className="space-y-2" data-testid="demo-bankruptcy-filings">
            {state.filings.map((f, i) => (
              <div
                key={`${f.debtor_id}-${f.case_number}`}
                className="grid grid-cols-1 md:grid-cols-[1.5fr_1fr_1fr_auto] gap-3 items-center bg-bg-primary border border-danger/40 rounded-[8px] p-4"
                data-testid={`demo-bankruptcy-row-${i}`}
              >
                <div>
                  <div className="text-white font-semibold text-[14px]">{f.debtor_name}</div>
                  <div className="text-text-secondary text-[12px] jea-mono">{f.case_number}</div>
                </div>
                <div>
                  <div className="jea-caption">Chapter</div>
                  <div className="text-white font-bold">Ch. {f.chapter}</div>
                </div>
                <div>
                  <div className="jea-caption">Filed</div>
                  <div className="jea-date">{formatDate(f.filed_date)}</div>
                </div>
                <JeaButton
                  size="sm"
                  variant="outline-neutral"
                  onClick={() => demo?.openSignUp("marking filings reviewed")}
                >
                  Mark Reviewed
                </JeaButton>
              </div>
            ))}
          </div>
        )}
      </JeaCard>
    </div>
  );
};

const StatTile = ({ label, value, color, small }) => (
  <JeaCard>
    <div className="jea-caption">{label}</div>
    <div
      className={`jea-mono font-extrabold ${small ? "text-[16px]" : "text-[28px]"} mt-2`}
      style={{ color: color || "#FFFFFF" }}
    >
      {value}
    </div>
  </JeaCard>
);

export default DemoBankruptcyPage;
